import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  bulkDownloadSpecsheetsOptimized,
  bulkDownloadSpecsheetsFromDeals,
  bulkDownloadSpecsheetsFromDealReports,
  getBulkDownloadStatus,
  downloadFromUrl,
  type BulkDownloadResult,
  type AsyncBulkDownloadResponse,
  type BulkDownloadStatusResponse,
} from '../../services/products_api'
import {
  bulkDownloadDocuments,
  getBulkDocumentDownloadStatus,
  type BulkDocumentDownloadResult,
  type AsyncBulkDocumentDownloadResponse,
  type BulkDocumentDownloadStatusResponse,
} from '../../services/documentsApi'

/**
 * Common status response type that works for both specsheets and documents.
 * Uses variant_count for specsheets and document_count for documents.
 */
type CommonStatusResponse =
  | BulkDownloadStatusResponse
  | BulkDocumentDownloadStatusResponse

/**
 * Common result type that works for both specsheets and documents.
 */
type CommonDownloadResult = BulkDownloadResult | BulkDocumentDownloadResult

/**
 * Common async response type that works for both specsheets and documents.
 */
type CommonAsyncResponse =
  | AsyncBulkDownloadResponse
  | AsyncBulkDocumentDownloadResponse

export interface DownloadItem {
  itemId: number
  itemName: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  error?: string
}

export type DownloadSource = 'variants' | 'deals' | 'deal_reports' | 'documents'

export type DownloadStatus =
  | 'idle'
  | 'preparing'
  | 'generating'
  | 'downloading'
  | 'completed'
  | 'failed'

interface BulkDownloadState {
  status: DownloadStatus
  items: DownloadItem[]
  progress: number // 0-100
  totalCount: number
  completedCount: number
  failedCount: number
  errorMessage?: string
  source?: DownloadSource
  requestId?: number // For async downloads
}

interface BulkDownloadContextType {
  state: BulkDownloadState
  startDownload: (
    itemIds: number[],
    itemNames: Map<number, string>,
    source?: DownloadSource
  ) => Promise<void>
  cancelDownload: () => void
  clearDownload: () => void
  isVisible: boolean
}

const BulkDownloadContext = createContext<BulkDownloadContextType | undefined>(
  undefined
)

const initialState: BulkDownloadState = {
  status: 'idle',
  items: [],
  progress: 0,
  totalCount: 0,
  completedCount: 0,
  failedCount: 0,
}

/** Polling interval in milliseconds */
const POLLING_INTERVAL = 1000

/**
 * Type guard to check if response is a BulkDownloadResult (blob)
 */
function isBulkDownloadResult(
  response: CommonDownloadResult | CommonAsyncResponse
): response is CommonDownloadResult {
  return 'blob' in response
}

/**
 * Type guard to check if response is an AsyncBulkDownloadResponse
 */
function isAsyncResponse(
  response: CommonDownloadResult | CommonAsyncResponse
): response is CommonAsyncResponse {
  return 'request_id' in response
}

export function BulkDownloadProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BulkDownloadState>(initialState)
  const [abortController, setAbortController] =
    useState<AbortController | null>(null)
  // Track polling state - requestId and items for the current async download
  const [pollingData, setPollingData] = useState<{
    requestId: number
    items: DownloadItem[]
    source?: DownloadSource
  } | null>(null)
  // Ref to track if download has been triggered (prevents duplicate downloads)
  const downloadTriggeredRef = useRef(false)

  const isVisible =
    state.status !== 'idle' ||
    state.items.length > 0 ||
    state.completedCount > 0

  /**
   * Get the status polling function based on source
   */
  const getStatusFn = useCallback(
    (
      source: DownloadSource | undefined,
      requestId: number
    ): Promise<CommonStatusResponse> => {
      if (source === 'documents') {
        return getBulkDocumentDownloadStatus(requestId)
      }
      return getBulkDownloadStatus(requestId)
    },
    []
  )

  /**
   * Poll for async download status using React Query
   * - refetchInterval ensures sequential polling (won't fire while request is in-flight)
   * - Returns false to stop polling when completed/failed/expired
   */
  const { data: statusResponse } = useQuery<CommonStatusResponse>({
    queryKey: [
      'bulk-download-status',
      pollingData?.requestId,
      pollingData?.source,
    ],
    queryFn: () => getStatusFn(pollingData?.source, pollingData!.requestId),
    enabled: !!pollingData?.requestId && !downloadTriggeredRef.current,
    refetchInterval: (query) => {
      const data = query.state.data
      // Stop polling if completed, failed, or expired
      if (
        data?.status === 'completed' ||
        data?.status === 'failed' ||
        data?.status === 'expired'
      ) {
        return false
      }
      return POLLING_INTERVAL
    },
  })

  /**
   * Handle status response changes from polling
   */
  useEffect(() => {
    if (!statusResponse || !pollingData || downloadTriggeredRef.current) return

    // Update progress
    setState((prev) => ({
      ...prev,
      progress: statusResponse.progress_percentage,
    }))

    // Handle completed status
    if (statusResponse.status === 'completed' && statusResponse.download_url) {
      // Mark download as triggered to prevent duplicates
      downloadTriggeredRef.current = true

      const handleDownload = async () => {
        setState((prev) => ({
          ...prev,
          status: 'downloading',
        }))

        try {
          // Generate default filename based on source (only for multiple files)
          // For single file downloads, use the server's filename from Content-Disposition
          const defaultFilename =
            pollingData.items.length > 1
              ? getDefaultFilename(pollingData.source)
              : undefined

          const result = await downloadFromUrl(
            statusResponse.download_url!,
            abortController?.signal ?? new AbortController().signal,
            (progress) => {
              setState((prev) => ({
                ...prev,
                progress: Math.min(progress, 99),
              }))
            },
            defaultFilename
          )

          // Trigger file download
          const url = window.URL.createObjectURL(result.blob)
          const link = document.createElement('a')
          link.href = url
          link.download = result.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          setState((prev) => ({
            ...prev,
            status: 'completed',
            progress: 100,
            completedCount: prev.totalCount,
            items: pollingData.items.map((item) => ({
              ...item,
              status: 'completed',
            })),
          }))
        } catch (error) {
          if (abortController?.signal.aborted) return

          setState((prev) => ({
            ...prev,
            status: 'failed',
            errorMessage: 'Failed to download file',
            progress: 100,
          }))
        } finally {
          setPollingData(null)
        }
      }

      handleDownload()
    }

    // Handle failed/expired status
    if (
      statusResponse.status === 'failed' ||
      statusResponse.status === 'expired'
    ) {
      setState((prev) => ({
        ...prev,
        status: 'failed',
        errorMessage:
          statusResponse.detail ||
          (statusResponse.status === 'expired'
            ? 'Download link expired'
            : 'Download failed'),
        progress: 100,
        items: pollingData.items.map((item) => ({
          ...item,
          status: 'failed',
          error: statusResponse.detail,
        })),
      }))
      setPollingData(null)
    }
  }, [statusResponse, pollingData, abortController])

  const startDownload = useCallback(
    async (
      itemIds: number[],
      itemNames: Map<number, string>,
      source: DownloadSource = 'variants'
    ) => {
      // Create abort controller for cancellation
      const controller = new AbortController()
      setAbortController(controller)

      // Initialize items
      const defaultLabel = getDefaultLabel(source)
      const items: DownloadItem[] = itemIds.map((id) => ({
        itemId: id,
        itemName: itemNames.get(id) || `${defaultLabel} ${id}`,
        status: 'pending',
      }))

      setState({
        status: 'preparing',
        items,
        progress: 0,
        totalCount: itemIds.length,
        completedCount: 0,
        failedCount: 0,
        source,
      })

      try {
        setState((prev) => ({
          ...prev,
          status: 'generating',
        }))

        // Handle documents separately
        if (source === 'documents') {
          const response = await bulkDownloadDocuments(
            itemIds,
            controller.signal,
            (progress) => {
              setState((prev) => ({
                ...prev,
                progress: Math.min(progress, 99),
              }))
            }
          )

          if (isBulkDownloadResult(response)) {
            // Download the file directly
            const url = window.URL.createObjectURL(response.blob)
            const link = document.createElement('a')
            link.href = url
            link.download = response.filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            setState((prev) => ({
              ...prev,
              status: 'completed',
              progress: 100,
              completedCount: prev.totalCount,
              items: prev.items.map((item) => ({
                ...item,
                status: 'completed',
              })),
            }))
          } else if (isAsyncResponse(response)) {
            // Async response - start polling via React Query
            downloadTriggeredRef.current = false
            setState((prev) => ({
              ...prev,
              requestId: response.request_id,
            }))
            setPollingData({ requestId: response.request_id, items, source })
          }
          return
        }

        // For deals/deal_reports, use async pattern (same as variants)
        if (source === 'deals' || source === 'deal_reports') {
          const downloadFn =
            source === 'deals'
              ? bulkDownloadSpecsheetsFromDeals
              : bulkDownloadSpecsheetsFromDealReports

          const response = await downloadFn(
            itemIds,
            controller.signal,
            (progress) => {
              setState((prev) => ({
                ...prev,
                progress: Math.min(progress, 99),
              }))
            }
          )

          // Check if it's a synchronous blob response (single variant)
          if (isBulkDownloadResult(response)) {
            // Download the file directly
            const url = window.URL.createObjectURL(response.blob)
            const link = document.createElement('a')
            link.href = url
            link.download = response.filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            setState((prev) => ({
              ...prev,
              status: 'completed',
              progress: 100,
              completedCount: prev.totalCount,
              items: prev.items.map((item) => ({
                ...item,
                status: 'completed',
              })),
            }))
          } else if (isAsyncResponse(response)) {
            // Async response - start polling via React Query
            downloadTriggeredRef.current = false
            setState((prev) => ({
              ...prev,
              requestId: response.request_id,
            }))
            setPollingData({ requestId: response.request_id, items, source })
          }
          return
        }

        // For variants, use the new async flow
        const response = await bulkDownloadSpecsheetsOptimized(
          itemIds,
          controller.signal,
          (progress) => {
            setState((prev) => ({
              ...prev,
              progress: Math.min(progress, 99),
            }))
          }
        )

        // Check if it's a synchronous blob response (single variant)
        if (isBulkDownloadResult(response)) {
          // Download the file directly
          const url = window.URL.createObjectURL(response.blob)
          const link = document.createElement('a')
          link.href = url
          link.download = response.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          setState((prev) => ({
            ...prev,
            status: 'completed',
            progress: 100,
            completedCount: prev.totalCount,
            items: prev.items.map((item) => ({
              ...item,
              status: 'completed',
            })),
          }))
        } else if (isAsyncResponse(response)) {
          // Async response - start polling via React Query
          downloadTriggeredRef.current = false
          setState((prev) => ({
            ...prev,
            requestId: response.request_id,
          }))
          setPollingData({ requestId: response.request_id, items, source })
        }
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          setState((prev) => ({
            ...prev,
            status: 'idle',
            items: [],
            progress: 0,
          }))
          return
        }

        // Extract error message from API response
        let errorMessage = 'Download failed'

        // Check if it's an axios error with response data
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { data?: unknown } }
          const responseData = axiosError.response?.data

          // Handle blob response (need to parse it)
          if (responseData instanceof Blob) {
            try {
              const text = await responseData.text()
              const json = JSON.parse(text)
              errorMessage = json.detail || json.error || errorMessage
            } catch {
              // If parsing fails, use default message
            }
          } else if (
            !(responseData instanceof ArrayBuffer) &&
            responseData &&
            typeof responseData === 'object'
          ) {
            // Handle JSON response
            const data = responseData as { detail?: string; error?: string }
            errorMessage = data.detail || data.error || errorMessage
          } else if (responseData instanceof ArrayBuffer) {
            const data = JSON.parse(new TextDecoder().decode(responseData)) as {
              detail?: string
              error?: string
            }
            errorMessage = data.detail || data.error || errorMessage
          }
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        setState((prev) => ({
          ...prev,
          status: 'failed',
          errorMessage,
          progress: 100,
          items: prev.items.map((item) => ({
            ...item,
            status: item.status === 'pending' ? 'failed' : item.status,
            error: item.status === 'pending' ? errorMessage : item.error,
          })),
        }))
      } finally {
        setAbortController(null)
      }
    },
    []
  )

  const cancelDownload = useCallback(() => {
    setPollingData(null)
    downloadTriggeredRef.current = false
    if (abortController) {
      abortController.abort()
      setAbortController(null)
    }
    setState(initialState)
  }, [abortController])

  const clearDownload = useCallback(() => {
    setPollingData(null)
    downloadTriggeredRef.current = false
    setState(initialState)
  }, [])

  return (
    <BulkDownloadContext.Provider
      value={{
        state,
        startDownload,
        cancelDownload,
        clearDownload,
        isVisible,
      }}
    >
      {children}
    </BulkDownloadContext.Provider>
  )
}

export function useBulkDownload(): BulkDownloadContextType {
  const context = useContext(BulkDownloadContext)
  if (context === undefined) {
    throw new Error(
      'useBulkDownload must be used within a BulkDownloadProvider'
    )
  }
  return context
}

/**
 * Get default label for items based on download source
 */
function getDefaultLabel(source: DownloadSource): string {
  switch (source) {
    case 'deals':
    case 'deal_reports':
      return 'Deal'
    case 'documents':
      return 'Document'
    case 'variants':
    default:
      return 'Variant'
  }
}

/**
 * Generate default filename based on download source and content type.
 * Format: {source}_{YYYY-MM-DD}.{ext}
 *
 * Examples:
 * - specsheets_2025-11-07.zip
 * - specsheet_2025-11-07.pdf (single)
 * - documents_2025-11-07.zip
 */
function getDefaultFilename(
  source: DownloadSource | undefined,
  contentType?: string
): string {
  const date = new Date().toISOString().split('T')[0]
  const isPdf = contentType?.includes('application/pdf')

  // For documents, always use plural form with .zip extension
  if (source === 'documents') {
    return `documents_${date}.zip`
  }

  // For specsheets (variants, deals, deal_reports)
  // - Single item (PDF): specsheet_YYYY-MM-DD.pdf
  // - Multiple items (ZIP): specsheets_YYYY-MM-DD.zip
  return isPdf ? `specsheet_${date}.pdf` : `specsheets_${date}.zip`
}
